import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Divider, Popconfirm, Table, message, Tooltip, Spin, Tabs } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { startVideoCall } from '@vantiva-sa/homesight_humaui/Store/videoCallStore';
import {
  CallNewIcon,
  CareIcon,
  ContactImage,
  SocialIcon,
  ResendEmailIcon,
  ContactsIcon,
  ManageNewIcon,
  CloseIcon,
  AddPlusIcon,
  EditNewIcon,
  PendingIcon,
  ErrorIcon,
  AttachmentLinkIcon,
  AddNewScheduleIcon,
  LinkedAccountIcon,
  NewHC200Icon,
} from '../Common/Icons';
import {
  formatFullNameDisplay,
  getUsername,
  isCare,
  isManage,
  isSocial,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { Actions } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import {
  KnownActionsList,
  isActionAllowed,
} from '@vantiva-sa/homesight_humaui/Utilities/AccessControl';
import { objectSort } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { getPermissions } from '@vantiva-sa/homesight_humaui/Store/permisssionsStore';
import useNotification from '../Hooks/useNotification';
import { filterNotifications, processNotifications } from '../HubDetails/HubDetailsUtils';
import moment from 'moment';
import { StarTwoTone } from '@ant-design/icons';
import NewCustomButton from '../Common/NewCustomButton';
import { useAssignStaffHandler } from '../Staff/NewAssignStaff/useAssignStaffHandler';
import AssignStaffModal from '../Staff/NewAssignStaff/AssignStaffModal';
import GroupsListView from '../Groups/GroupListView';

const formatContacts = (contacts) =>
  contacts.map((c) => {
    return { key: c.useruuid, ...c };
  });

const ListContacts = (props) => {
  const {
    hubRecord,
    contacts,
    fetchingContacts,
    onRemove,
    onActionClick,
    openSubMenu = false,
    userHubsData,
  } = props;

  const dispatch = useDispatch();
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const userApi = useSelector((state) => state.apis.userApi);
  const contactList = useMemo(() => formatContacts(contacts), [contacts]);
  const items = useMemo(() => {
    const tabItems = [
      { key: Actions.listContacts, label: trans('CONTACTS') },
      { key: Actions.viewPrinipalGroupDetails, label: trans('GROUPS') },
    ];
    return tabItems.sort(objectSort('label'));
  }, []);
  const permissions = useSelector(getPermissions());
  const [favorites, setFavorites] = useState([]);
  const [getFavorites, setGetFavorites] = useState([]);
  const [selectedHub, setSelectedHub] = useState('');
  const [linkAccountChecked, setLinkAccountChecked] = useState(false);
  const {
    isFirstModalOpen,
    openAssignStaffModal,
    handleCloseFirstModal,
    selectedStaff,
    handleAddStaff,
    mOrderedStaffList,
    fetchingStaffList,
    formData,
    handleAssignStaff,
    handleRoleSelection,
    handleInputChange,
    handleOpenSecondModal,
    handleCloseSecondModal,
    isSecondModalOpen,
    setSelectedStaff,
    role,
    setSearchNameApplied,
    searchNameApplied,
    setSearchNameText,
    searchNameText,
    accountContacts,
    accountfetchingContacts,
    accountfetchContacts,
  } = useAssignStaffHandler(hubRecord, onActionClick, openSubMenu);

  const [selectedTab, setSelectedTab] = useState(Actions.listContacts);

  const handleTabChange = (selectedTab) => {
    setSelectedTab(selectedTab);
  };

  const allowCreateEvent = useMemo(
    () => isActionAllowed(hubRecord?.rolename, KnownActionsList.create_events, permissions),
    [hubRecord, permissions]
  );
  const allowCallContact = useMemo(
    () => isActionAllowed(hubRecord?.rolename, KnownActionsList.call_contacts, permissions),
    [hubRecord, permissions]
  );
  const allowManageContact = useMemo(
    () => isActionAllowed(hubRecord?.rolename, KnownActionsList.manage_contacts, permissions),
    [hubRecord, permissions]
  );

  const onResendEmailHandler = (contactuseruuid) => {
    const errorCallback = (error) => {
      const msg = `Error Sending Email details`;
      console.error(msg, error);
      // message.error(msg);
    };
    const successCallback = (data) => {
      const msg = `Email Sent`;
      // message.success(msg);
      onActionClick(null, null, true)();
    };
    try {
      const user = {
        useruuid: contactuseruuid,
        principaluseruuid: hubRecord?.useruuid,
        updateprofile: true,
      };
      userApi.updateUser(
        user,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const sortedContactsList = useMemo(() => {
    return [...contactList].sort(objectSort('firstname'));
  }, [contactList]);

  // Notifications related code
  const [contactNotifInfo, setContactNotifInfo] = useState({});
  const startdatetime = useMemo(() => moment().subtract(3, 'day').format(), []);
  const isCareOrManageRole = isCare(hubRecord) || isManage(hubRecord);

  const [notifications] = useNotification({
    useruuid: hubRecord?.useruuid,
    startdatetime: startdatetime,
    callhistory: true,
  });

  const ongetFavoriteContact = () => {
    const errorCallback = (error) => {
      const msg = `Error Feching Favorite Contact details`;
      console.error(msg, error);
      // message.error(msg);
    };
    const successCallback = (data) => {
      // Set only the first user's useruuid as favorite
      const firstFavorite = data.contacts.length > 0 ? data.contacts[0].useruuid : null;
      setGetFavorites(firstFavorite ? [firstFavorite] : []);
      setFavorites(firstFavorite ? [firstFavorite] : []);
    };
    try {
      const useruuid = hubRecord?.useruuid;
      userApi.getFavoriteContacts(
        useruuid,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const handleFavoriteChange = (useruuid) => {
    setFavorites((prevFavorites) => {
      if (prevFavorites.includes(useruuid)) {
        // Removing from favorites
        onUpdateFavoriteContacts([{ useruuid, favorite: false, removeContact: true }]);
        return prevFavorites.filter((id) => id !== useruuid);
      } else if (prevFavorites.length === 0) {
        // Setting as the only favorite
        onUpdateFavoriteContacts([{ useruuid, favorite: true }]);
        return [useruuid];
      } else {
        // Toggling favorites: remove the old favorite and set the new one
        const previousFavorite = prevFavorites[0];
        onUpdateFavoriteContacts([
          { useruuid: previousFavorite, favorite: false },
          { useruuid, favorite: true },
        ]);
        return [useruuid];
      }
    });
  };

  const onUpdateFavoriteContacts = (contacts) => {
    const errorCallback = (error) => {
      const msg = `Error Updating Favorite contacts`;
      console.error(msg, error);
      // message.error(msg);
    };
    const successCallback = (data) => {
      const msg = `Favorite Contact Updated Successfully`;
      const removeMsg = `Removed from Favorite Contacts Successfully`;
      if (contacts[0].removeContact) {
        // message.error(removeMsg);
        console.error(removeMsg);
      }
      ongetFavoriteContact();
    };
    try {
      const useruuidToUpdate = hubRecord?.useruuid;
      userApi.updateFavoriteContacts(
        useruuidToUpdate,
        { contacts },
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  useEffect(() => {
    ongetFavoriteContact();
  }, []);

  useEffect(() => {
    if (isActionAllowed(hubRecord?.rolename, KnownActionsList.call_history, permissions)) {
      const filteredList = filterNotifications(notifications);
      const processedList = processNotifications(filteredList, hubRecord, trans);
      setContactNotifInfo(processedList);
    }
  }, [hubRecord, permissions, notifications, sortedContactsList, trans]);

  const renderUserType = (hub) =>
    isManage(hub) ? (
      <>
        <ManageNewIcon />
        <span className="type-text">{trans('MANAGE')}</span>
      </>
    ) : isCare(hub) ? (
      <>
        <CareIcon style={{ stroke: '#1C1C1C' }} />
        <span className="type-text">{trans('CARE')}</span>
      </>
    ) : isSocial(hub) ? (
      <>
        <SocialIcon style={{ stroke: '#1C1C1C' }} />
        <span className="type-text">{trans('SOCIAL')}</span>
      </>
    ) : (
      <div></div>
    );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (_, record) => {
        let mIcon = null;
        if (record.useruuid in contactNotifInfo) {
          mIcon = contactNotifInfo[record.useruuid].icon;
        }
        return (
          <div className="user-info-container">
            <div className="icons-container">
              {record.profilePic64 ? (
                <img src={record.profilePic64} className="contact-user-profile-pic image" alt="" />
              ) : (
                <ContactImage className="contact-user-profile-pic svg" />
              )}
              {mIcon && <div>{mIcon}</div>}
              {getFavorites.includes(record.useruuid) && record.emailVerified === true && (
                <div>
                  <StarTwoTone className="favorite-icon" twoToneColor="#ffd700" />
                </div>
              )}
            </div>
            <div className="name-relation-container">
              <label
                className={`${
                  getFavorites.includes(record.useruuid) ? 'favorite-name name' : 'name'
                }`}
              >
                {formatFullNameDisplay(record?.firstname, record?.lastname)}
              </label>
              <div className="relationship">{record?.targetrelationship}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Role & Status',
      key: 'role_status',
      render: (_, record) => (
        <div className="role-status-container">
          <div className="usertype-container">{renderUserType(record)}</div>
          <div
            className={`item-counter entity-actions-container ${
              record.emailVerified === false
                ? 'list-contact-pending-container-width'
                : record.usertype === 'Principal'
                  ? 'list-contact-linked-account-container-width'
                  : ''
            }`}
          >
            {record.emailVerified === false ? (
              <>
                <Divider type="vertical" className="divider" />
                <PendingIcon />
              </>
            ) : record.usertype === 'Principal' ? (
              <>
                <Divider type="vertical" className="divider" />
                <div className="contact-list-linked-account-block">
                  <Tooltip title={trans('LINKEDACCOUNTDESCRIPTION')} placement="bottom">
                    <span className="contact-list-linked-account-icon-span">
                      <LinkedAccountIcon className="contact-list-linked-account-icon" />
                    </span>
                  </Tooltip>
                  <NewHC200Icon
                    className="contact-list-linked-account-hc200-device-icon"
                    status={
                      record?.deviceconnectstatus?.toLowerCase() === 'connected'
                        ? 'connected'
                        : 'disconnected'
                    }
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'actions',
      render: (_, record) => (
        <div className="entity-actions-container zoom-control">
          <Divider type="vertical" className="divider" />
          {allowManageContact && isManage(hubRecord) && record.emailVerified === false ? (
            <Tooltip title={trans('RESENDEMAIL')} placement="bottom">
              <div className="entity-action">
                <ResendEmailIcon
                  className="resend-icon"
                  onClick={() => {
                    onResendEmailHandler(record.useruuid);
                  }}
                />
              </div>
            </Tooltip>
          ) : (
            record?.usertype !== 'Principal' && (
              <div className="entity-action">
                <StarTwoTone
                  className={favorites.includes(record.useruuid) ? 'favorite' : 'notfavorite'}
                  onClick={isCareOrManageRole ? () => handleFavoriteChange(record.useruuid) : null}
                />
              </div>
            )
          )}
          {!record.providerName &&
          record.useruuid !== currentUser.useruuid &&
          allowCallContact &&
          record.emailVerified === true &&
          record.callpermission &&
          record.usertype !== 'Principal' ? (
            <Tooltip title={trans('VIDEOCALL')} placement="bottom">
              <div
                className="entity-action"
                onClick={() => dispatch(startVideoCall({ callees: [record], hubRecord }))}
              >
                <CallNewIcon className="call-icon" />
              </div>
            </Tooltip>
          ) : (
            <div className="empty-action" />
          )}
          {allowCreateEvent && record.emailVerified === true && record.usertype !== 'Principal' ? (
            <Tooltip title={trans('SCHEDULEEVENT')} placement="bottom">
              <div className="entity-action clickable-item ">
                <AddNewScheduleIcon
                  className="contact-schedule-icon"
                  onClick={onActionClick(Actions.addEvent, record, openSubMenu ? true : false)}
                />
              </div>
            </Tooltip>
          ) : (
            <div className="empty-action" />
          )}
          {allowManageContact ? (
            <Tooltip title={trans('EDIT')} placement="bottom">
              <div className="entity-action">
                <EditNewIcon
                  className="edit-icon"
                  onClick={() => {
                    const isPrincipal = record?.usertype === 'Principal';
                    onActionClick(
                      isPrincipal ? Actions.editLinkedAccount : Actions.editContact,
                      record
                    )();
                  }}
                />
              </div>
            </Tooltip>
          ) : (
            <div className="empty-action" />
          )}
          {allowManageContact || record.useruuid === currentUser.useruuid ? (
            <Popconfirm
              placement="topRight"
              okText={trans('YES')}
              cancelText={trans('NO')}
              onConfirm={() => {
                onRemove(record.useruuid);
              }}
              title={trans('AREYOUSURETOREMOVECONTACT')}
            >
              <Tooltip title={trans('REMOVE_L')} placement="bottom">
                <div className="entity-action">
                  <CloseIcon className="remove-icon" />
                </div>
              </Tooltip>
            </Popconfirm>
          ) : (
            <div className="empty-action" />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="contacts-list-wrapper page-content-wrapper custom-page-wrapper">
      <AssignStaffModal
        isFirstModalOpen={isFirstModalOpen}
        handleCloseFirstModal={handleCloseFirstModal}
        fetchingStaffList={fetchingStaffList}
        selectedStaff={selectedStaff}
        mOrderedStaffList={userHubsData}
        handleAddStaff={handleAddStaff}
        formData={formData}
        handleInputChange={handleInputChange}
        selectedUser={getUsername(selectedHub)}
        handleAssignStaff={handleAssignStaff}
        handleRoleSelection={handleRoleSelection}
        handleOpenSecondModal={handleOpenSecondModal}
        handleCloseSecondModal={handleCloseSecondModal}
        isSecondModalOpen={isSecondModalOpen}
        setSelectedStaff={setSelectedStaff}
        role={role}
        hubRecord={selectedHub}
        setSearchNameApplied={setSearchNameApplied}
        searchNameApplied={searchNameApplied}
        setSearchNameText={setSearchNameText}
        searchNameText={searchNameText}
        linkAccountChecked={linkAccountChecked}
        setLinkAccountChecked={setLinkAccountChecked}
        userHubsData={userHubsData}
        onActionClick={onActionClick}
        accountContacts={accountContacts}
        contactsList={contacts}
        fetchingContactsList={fetchingContacts}
      />
      <div className="page-wrapper">
        <header className="page-header-container">
          <label
            className="page-header"
            style={{ color: '#113E57', fontSize: '28px', fontWeight: '700' }}
          >
            <ContactsIcon className="addcontacts-icon" />
            <div className="title-text">{trans('CONTACTS')}</div>
          </label>
        </header>
        <Tabs
          defaultActiveKey={Actions.listContacts}
          items={items}
          onChange={handleTabChange}
          className="contacts_tab"
        />
        {selectedTab === Actions.listContacts ? (
          <div className="contact-tab-selection">
            <div className="contacts-info">
              <div className="contacts-count-info">
                <span className="contacts-count">{`${trans('TOTAL')} (${sortedContactsList.length})`}</span>
                <span className="contacts-list-subtitle">
                  {trans('CONTACTSSORTEDBYALPHABETICALORDER')}
                </span>
              </div>
              <div className="contact-action-buttons">
                {allowManageContact && (
                  <div className="link-account">
                    <NewCustomButton
                      className="link-btn"
                      onClick={() => {
                        setSelectedHub(hubRecord);
                        openAssignStaffModal();
                        setLinkAccountChecked(true);
                      }}
                      label={trans('LINKACCOUNT')}
                      icon={<AttachmentLinkIcon className="cancel-icon" />}
                    />
                  </div>
                )}
                {allowManageContact ? (
                  <div className="add-contact-icon">
                    <Tooltip title={trans('ADDCONTACT')}>
                      <div
                        className="add-contact-button"
                        onClick={onActionClick(Actions.inviteContact, null)}
                      >
                        <AddPlusIcon className="contact-add-icon" />
                        <span className="add-contact-label">{trans('ADDCONTACT')}</span>
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="empty-action" />
                )}
              </div>
            </div>
            <Spin spinning={fetchingContacts} style={{ marginTop: 40 }}>
              {!fetchingContacts && sortedContactsList.length === 0 ? (
                <div className="no-contacts-container">{trans('YOURCONTACTSEEMSEMPTY')}</div>
              ) : (
                <Table
                  showHeader={false}
                  dataSource={sortedContactsList}
                  columns={columns}
                  loading={false}
                  bordered={false}
                  pagination={false}
                  className="entity-table contacts-table"
                  rowClassName={() => 'custom-row'}
                  components={{
                    body: {
                      row: ({ children, ...restProps }) => (
                        <tr {...restProps} className="contact-card-row">
                          <td colSpan={columns.length} className="contact-details-card">
                            <div
                              className={`custom-row-wrapper ${
                                sortedContactsList.length === 0 ? 'centered' : 'spaced'
                              }`}
                            >
                              {children}
                            </div>
                          </td>
                        </tr>
                      ),
                    },
                  }}
                />
              )}
            </Spin>
          </div>
        ) : (
          <GroupsListView
            useruuid={hubRecord?.useruuid}
            actionView={Actions.viewPrinipalGroupDetails}
          />
        )}
      </div>
    </div>
  );
};

ListContacts.propTypes = {
  header: PropTypes.arrayOf(PropTypes.object),
  hubRecord: PropTypes.object,
  contacts: PropTypes.arrayOf(PropTypes.object),
  fetchingContacts: PropTypes.bool,
  onActionClick: PropTypes.func,
  onRemove: PropTypes.func,
};

export default ListContacts;
